// SPDX-License-Identifier: Apache-2.0

import React, { useContext, useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { utils } from "ethers";
import { AppContext } from "../../AppContext";
import { indexGuardrailsByAsset } from "../../api/GetGuardrailsResponse";
import EthereumAsset, { AssetID } from "../../api/types/ethereumAsset";
import { NervosAsset } from "../../api/types/nervosAsset";
import { Asset } from "../../api/types/channelState";
import { ethChainID, Hubs, volumeLimitUSD } from "../../constants/Constants";

/** The chain ID the Nervos assets live on. */
const NervosChainID = 3n;

/** One asset with the most of it a single swap may move. */
type AssetMaximum = {
  asset: EthereumAsset | NervosAsset;
  /** Whole units, already lowered to the volume limit where that binds. */
  units: number;
};

interface LimitsAndLiquidityProps {
  /** The assets the backend offers, as the swap form already fetched them. */
  assets: Map<AssetID, Asset>;
}

/** One asset's guardrail, reduced to what deriving the ceiling needs. */
export type GuardrailCeiling = {
  /** Maximum swappable amount in base units, as the hub computed it. */
  threshold: string;
  decimals: number;
  /** USD per whole unit. */
  price: number;
};

/**
 * Recovers the hub's per-swap USD ceiling and lowers it to the volume limit
 * this build carries.
 *
 * The hub never sends the ceiling, it sends thresholds built from it as
 * ceiling/price * 10^decimals — so multiplying back gives the configured value.
 * Every asset encodes the same one, so they should agree; the smallest is taken
 * so a rounding difference can only understate the limit.
 *
 * Returns undefined when no threshold can be read. The volume limit alone is
 * not enough to answer with: it is the higher of the two whenever the guardrail
 * is what actually binds, so showing it by itself would advertise a ceiling the
 * hub does not allow.
 */
export function deriveCapUSD(
  ceilings: GuardrailCeiling[],
  volumeLimit: number,
): number | undefined {
  const usd = ceilings
    .filter((c) => c.price > 0)
    .map(
      (c) => parseFloat(utils.formatUnits(c.threshold, c.decimals)) * c.price,
    )
    .filter((v) => isFinite(v) && v > 0);
  if (usd.length === 0) return undefined;
  const guardrail = Math.min(...usd);
  return volumeLimit > 0 ? Math.min(guardrail, volumeLimit) : guardrail;
}

/**
 * The most of one asset a single swap may move: the hub's threshold, lowered
 * to the volume limit when that is the stricter of the two. This is the only
 * figure the page shows per asset — never a balance, so nothing about the
 * hub's liquidity can be read off it.
 */
export function deriveAssetMaximum(
  ceiling: GuardrailCeiling,
  volumeLimit: number,
): number | undefined {
  const units = parseFloat(
    utils.formatUnits(ceiling.threshold, ceiling.decimals),
  );
  if (!isFinite(units) || units <= 0) return undefined;
  if (volumeLimit > 0 && ceiling.price > 0) {
    return Math.min(units, volumeLimit / ceiling.price);
  }
  return units;
}

const usdFormat = (usd: number): string =>
  usd.toLocaleString("en-US", {
    style: "currency",
    currency: "USD",
    // Currency style would otherwise force "$50.00" on a ceiling that is a
    // round number, which reads as more precision than the figure has.
    minimumFractionDigits: 0,
    maximumFractionDigits: usd >= 100 ? 0 : 2,
  });

const amountFormat = (units: number): string =>
  units.toLocaleString("en-US", {
    maximumFractionDigits: units >= 1000 ? 0 : 4,
  });

/**
 * Shows, above the swap form, the most a single swap may move per asset.
 *
 * The ceiling used to surface only as a validation error once the user had
 * already typed too much. The figures come from the guardrail response the app
 * already fetches — the hub builds each threshold as ceiling/price * 10^decimals
 * — so nothing is needed from the backend that is not already deployed.
 *
 * Deliberately NOT shown: the hub's balances. They were displayed here for a
 * few days and taken down on request (2026-09-08): liquidity is operational
 * information and belongs in the hub's Prometheus metrics, not on the page. A
 * swap the hub cannot cover is still rejected with its own error.
 */
export default function LimitsAndLiquidity(props: LimitsAndLiquidityProps) {
  const { t } = useTranslation();
  const ctx = useContext(AppContext);

  const [capUSD, setCapUSD] = useState<number | undefined>(undefined);
  const [maxima, setMaxima] = useState<AssetMaximum[]>([]);

  // Fetched once per asset list: the ceiling is a hub start-up flag and the
  // prices behind it are cached hub-side for longer than a swap session lasts.
  useEffect(() => {
    // Stellar is not part of the mainnet product, and an Ethereum asset on
    // another chain is not something this hub can pay out.
    const tracked = Array.from(props.assets.values()).filter(
      (a): a is EthereumAsset | NervosAsset =>
        (a instanceof EthereumAsset && a.chainID === ethChainID) ||
        (a instanceof NervosAsset && a.chainID === NervosChainID),
    );
    if (tracked.length === 0) return;

    let cancelled = false;

    const load = async () => {
      const resp = await ctx.client.getGuardrails(
        tracked,
        Hubs.get(ethChainID)!.address,
      );
      if (cancelled || !resp) return;
      const guardrails = indexGuardrailsByAsset(resp.guardrails, tracked);

      const decimals = await Promise.all(
        tracked.map((a) => ctx.client.getDecimals(a.toChannelAsset())),
      );
      if (cancelled) return;

      const found: AssetMaximum[] = [];
      const ceilings: GuardrailCeiling[] = [];
      tracked.forEach((asset, i) => {
        const guardrail = guardrails.get(asset.assetID());
        const decimal = decimals[i];
        if (!guardrail || decimal === undefined || guardrail.price <= 0) return;
        const ceiling: GuardrailCeiling = {
          threshold: guardrail.threshold,
          decimals: decimal,
          price: guardrail.price,
        };
        ceilings.push(ceiling);
        const units = deriveAssetMaximum(ceiling, volumeLimitUSD);
        if (units !== undefined) found.push({ asset, units });
      });

      setMaxima(found);
      setCapUSD(deriveCapUSD(ceilings, volumeLimitUSD));
    };

    load().catch((err) => console.error("swap limits unavailable:", err));
    return () => {
      cancelled = true;
    };
  }, [props.assets]);

  // Nothing read yet, or nothing readable: say nothing rather than show a row
  // of dashes that reads like there is no limit.
  if (maxima.length === 0) return null;

  return (
    <div className={"limits-liquidity"}>
      <div className={"limits-liquidity-row"}>
        <span className={"limits-liquidity-label"}>{t("maxPerSwap")}</span>
        <span className={"limits-liquidity-value"}>
          {maxima
            .map((m) => `${amountFormat(m.units)} ${m.asset.code}`)
            .join(" · ")}
          {capUSD !== undefined && (
            <span className={"limits-liquidity-usd"}>~{usdFormat(capUSD)}</span>
          )}
        </span>
      </div>
    </div>
  );
}
